import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Row,
  Col,
  Divider,
  Tooltip,
  Space,
  Button,
  List,
  Card,
  Tag,
  Typography,
} from "antd";
import { 
  InfoCircleOutlined, 
  PlusOutlined, 
  CopyOutlined,
  DeleteOutlined,
  FileImageOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import SkuWizard from "./SkuWizard";
import { CDNFilePicker } from "../CDNBrowser";
import FileUploader from "../shared/FileUploader";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  findDuplicateSku,
  hasSkuChanged,
  isValidSkuFormat,
  normalizeComparableSku,
} from "./skuUtils";

const { TextArea } = Input;
const { Text } = Typography;

const layout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
};

const layoutWithLongLabels = {
  labelCol: { span: 13 },
  wrapperCol: { span: 14 },
};

const formatPrice = (value) => {
  if (!value) return "€ 0,00";
  return `€ ${value.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const parsePrice = (value) => {
  if (!value) return 0;
  return Number(value.replace(/[^0-9.-]+/g, ""));
};

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link'
];


const defaultValues = {
  name: "",
  description: "",
  price: 0,
  minutes_of_training: 0,
  is_product_line: false,
  is_main: false,
  una_tantum: false,
  power_by: "",
  power_by_link: "",
  order: 0,
  module_category_pointer: undefined,
  quantity: false,
  included_quantity: 0,
  charge_by_quantity: false,
  price_for_quantity: 0,
  sku: "",
  unit_of_measure: "",
  is_active: true,
  relatedServices: [],
  includedServices: [],
  targets: [],
  included_quantity_max_step: false,
  included_quantity_max_step_multiplier: 1,
  included_quantity_max_step_minimum: 0,
  youtube_video_url: "",
  landing_page_url: "",
  cover_image_url: "",
  features: [],
  html_description: "",
};

const errorMessageStyle = {
  color: "#ff4d4f",
  textAlign: "center",
  marginTop: "16px",
  padding: "8px",
  border: "1px solid #ff4d4f",
  borderRadius: "4px",
  display: "none",
};

const ModuleEditForm = ({
  form,
  moduleId,
  draftSeed,
  sessionKey,
  categories,
  services,
  moduleTargets,
  onIconChange,
}) => {
  const [quantityEnabled, setQuantityEnabled] = React.useState(
    draftSeed.quantity || defaultValues.quantity
  );
  
  const [isProductLine, setIsProductLine] = React.useState(
    draftSeed.is_product_line || defaultValues.is_product_line
  );

  const [features, setFeatures] = useState(
    draftSeed.features || []
  );
  const [filePickerVisible, setFilePickerVisible] = useState(false);
  const [filePickerField, setFilePickerField] = useState(null);
  const [filePickerFeatureIndex, setFilePickerFeatureIndex] = useState(null);
  const [htmlDescription, setHtmlDescription] = useState(
    draftSeed.html_description || defaultValues.html_description
  );
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [iconUrl, setIconUrl] = useState(draftSeed.icon?.url || null);
  const [iconFileName, setIconFileName] = useState(draftSeed.icon?.name || null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState(null);
  const updateIcon = (url, name) => {
    setIconUrl(url);
    setIconFileName(name);
    if (onIconChange) onIconChange(url, name);
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      name: draftSeed.name || defaultValues.name,
      description: draftSeed.description || defaultValues.description,
      price: draftSeed.price || defaultValues.price,
      minutes_of_training:
        draftSeed.minutes_of_training || defaultValues.minutes_of_training,
      is_product_line: draftSeed.is_product_line || defaultValues.is_product_line,
      is_main: draftSeed.is_main || defaultValues.is_main,
      una_tantum: draftSeed.una_tantum || defaultValues.una_tantum,
      power_by: draftSeed.power_by || defaultValues.power_by,
      power_by_link: draftSeed.power_by_link || defaultValues.power_by_link,
      order: draftSeed.order || defaultValues.order,
      module_category_pointer:
        draftSeed.module_category_pointer?.objectId ||
        defaultValues.module_category_pointer,
      quantity: draftSeed.quantity || defaultValues.quantity,
      included_quantity:
        draftSeed.included_quantity || defaultValues.included_quantity,
      charge_by_quantity:
        draftSeed.charge_by_quantity || defaultValues.charge_by_quantity,
      price_for_quantity:
        draftSeed.price_for_quantity || defaultValues.price_for_quantity,
      sku: draftSeed.sku || defaultValues.sku,
      unit_of_measure: draftSeed.unit_of_measure || defaultValues.unit_of_measure,
      is_active:
        draftSeed.is_active !== undefined
          ? draftSeed.is_active
          : defaultValues.is_active,
      relatedServices:
        draftSeed.related_service_relation?.edges?.map(
          (edge) => edge.node.objectId
        ) || [],
      includedServices:
        draftSeed.included_service_relation?.edges?.map(
          (edge) => edge.node.objectId
        ) || [],
      targets:
        draftSeed.target_relation?.edges?.map(
          (edge) => edge.node.objectId
        ) || [],
      included_quantity_max_step:
        draftSeed.included_quantity_max_step ||
        defaultValues.included_quantity_max_step,
      included_quantity_max_step_multiplier:
        draftSeed.included_quantity_max_step_multiplier ||
        defaultValues.included_quantity_max_step_multiplier,
      included_quantity_max_step_minimum:
        draftSeed.included_quantity_max_step_minimum ||
        defaultValues.included_quantity_max_step_minimum,
      youtube_video_url: draftSeed.youtube_video_url || defaultValues.youtube_video_url,
      landing_page_url: draftSeed.landing_page_url || defaultValues.landing_page_url,
      cover_image_url: draftSeed.cover_image_url || defaultValues.cover_image_url,
      html_description: draftSeed.html_description || defaultValues.html_description,
      features: draftSeed.features || defaultValues.features,
    });

    setQuantityEnabled(draftSeed.quantity || defaultValues.quantity);
    setIsProductLine(draftSeed.is_product_line || defaultValues.is_product_line);
    setFeatures(draftSeed.features || []);
    setHtmlDescription(draftSeed.html_description || defaultValues.html_description);
    setShowHtmlSource(false);
    setFilePickerVisible(false);
    setFilePickerField(null);
    setFilePickerFeatureIndex(null);
    updateIcon(draftSeed.icon?.url || null, draftSeed.icon?.name || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, draftSeed, sessionKey]);

  const handleFieldsChange = (changedFields) => {
    const values = form.getFieldsValue();
    const errorElement = document.getElementById("quantityError");

    if (changedFields.some((field) => field.name[0] === "quantity")) {
      setQuantityEnabled(values.quantity);
    }
    
    if (changedFields.some((field) => field.name[0] === "is_product_line")) {
      setIsProductLine(values.is_product_line);
    }

    if (
      changedFields.some(
        (field) => field.name[0] === "included_quantity_max_step"
      ) &&
      values.included_quantity_max_step
    ) {
      const updates = {};

      if (values.included_quantity) {
        updates.included_quantity = 0;
      }

      if (!values.included_quantity_max_step_multiplier) {
        updates.included_quantity_max_step_multiplier =
          defaultValues.included_quantity_max_step_multiplier;
      }

      if (Object.keys(updates).length > 0) {
        form.setFieldsValue(updates);
        Object.assign(values, updates);
      }
    }

    if (values.included_quantity && values.included_quantity_max_step) {
      if (errorElement) errorElement.style.display = "block";
      form.setFields([
        {
          name: "included_quantity",
          errors: [
            "Non può essere abilitato insieme a Quantità Inclusa Max Step",
          ],
        },
        {
          name: "included_quantity_max_step",
          errors: ["Non può essere abilitato insieme a Quantità"],
        },
      ]);
    } else {
      if (errorElement) errorElement.style.display = "none";
      form.setFields([
        {
          name: "included_quantity",
          errors: [],
        },
        {
          name: "included_quantity_max_step",
          errors: [],
        },
      ]);
    }
  };

  // Filter out the current module from services list
  const filteredServices = services?.filter(
    (service) => service.objectId !== draftSeed.objectId
  );
  const watchedName = Form.useWatch("name", form);
  const watchedSku = Form.useWatch("sku", form);
  const watchedIncludedServices = Form.useWatch("includedServices", form);
  const watchedRelatedServices = Form.useWatch("relatedServices", form);
  const includedQuantityMaxStepEnabled = Form.useWatch(
    "included_quantity_max_step",
    form
  );
  const selectedIncludedServices = useMemo(
    () => watchedIncludedServices || [],
    [watchedIncludedServices]
  );
  const selectedRelatedServices = useMemo(
    () => watchedRelatedServices || [],
    [watchedRelatedServices]
  );
  const currentSku = watchedSku || "";
  const initialSku = draftSeed.sku || "";
  const relatedLabel = isProductLine
    ? "Moduli del Servizio"
    : "Moduli Correlati";
  const relatedPlaceholder = isProductLine
    ? "Seleziona i moduli principali del servizio"
    : "Seleziona i moduli correlati";

  const serviceGroups = useMemo(() => {
    const groups = new Map();

    filteredServices?.forEach((service) => {
      const groupKey = service.is_product_line
        ? "Servizi"
        : service.module_category_pointer?.name || "Senza categoria";

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }

      groups.get(groupKey).push(service);
    });

    return Array.from(groups.entries()).map(([label, items]) => ({
      label,
      items,
    }));
  }, [filteredServices]);

  const servicesById = useMemo(
    () =>
      new Map(
        (services || []).map((service) => [
          service.objectId,
          service,
        ])
      ),
    [services]
  );
  const skuDuplicateMatch = useMemo(
    () => findDuplicateSku(currentSku, services, draftSeed.objectId),
    [currentSku, draftSeed.objectId, services]
  );
  const skuChanged = useMemo(
    () => hasSkuChanged(currentSku, initialSku),
    [currentSku, initialSku]
  );
  const skuFormatInvalid = useMemo(
    () => Boolean(currentSku) && skuChanged && !isValidSkuFormat(currentSku),
    [currentSku, skuChanged]
  );
  const skuLegacyUnchanged = useMemo(
    () => Boolean(currentSku) && !skuChanged && !isValidSkuFormat(currentSku),
    [currentSku, skuChanged]
  );

  const selectedIncludedModules = useMemo(
    () =>
      selectedIncludedServices
        .map((serviceId) => servicesById.get(serviceId))
        .filter(Boolean),
    [selectedIncludedServices, servicesById]
  );
  const selectedRelatedModules = useMemo(
    () =>
      selectedRelatedServices
        .map((serviceId) => servicesById.get(serviceId))
        .filter(Boolean),
    [selectedRelatedServices, servicesById]
  );

  const relationshipWarnings = useMemo(() => {
    const warnings = [];
    const includedMainModules = selectedIncludedModules.filter(
      (service) => service.is_main
    );
    const relatedMainModules = selectedRelatedModules.filter(
      (service) => service.is_main
    );
    const productLineLegacyModules = selectedRelatedModules.filter(
      (service) => isProductLine && !service.is_product_line && !service.is_main
    );
    const inactiveRelatedModules = selectedRelatedModules.filter(
      (service) => !service.is_active
    );

    if (!isProductLine && includedMainModules.length > 0) {
      warnings.push({
        key: "included-main",
        type: "error",
        message: `Negli inclusi automatici ci sono moduli principali: ${includedMainModules
          .map((service) => service.name)
          .join(", ")}.`,
      });
    }

    if (!isProductLine && relatedMainModules.length > 0) {
      warnings.push({
        key: "related-main",
        type: "warning",
        message: `Tra i moduli correlati ci sono moduli principali: ${relatedMainModules
          .map((service) => service.name)
          .join(", ")}.`,
      });
    }

    if (isProductLine && productLineLegacyModules.length > 0) {
      warnings.push({
        key: "product-line-legacy",
        type: "warning",
        message: `Questo servizio contiene moduli non principali collegati direttamente: ${productLineLegacyModules
          .map((service) => service.name)
          .join(", ")}.`,
      });
    }

    if (inactiveRelatedModules.length > 0) {
      warnings.push({
        key: "inactive-related",
        type: "warning",
        message: `Sono presenti moduli inattivi collegati: ${inactiveRelatedModules
          .map((service) => service.name)
          .join(", ")}.`,
      });
    }

    return warnings;
  }, [isProductLine, selectedIncludedModules, selectedRelatedModules]);

  const handleSkuBlur = (event) => {
    const normalizedValue = normalizeComparableSku(event.target.value);

    if (normalizedValue !== event.target.value) {
      form.setFieldValue("sku", normalizedValue);
    }
  };

  const handleWizardApply = (nextSku) => {
    form.setFieldValue("sku", nextSku);
    form.validateFields(["sku"]).catch(() => {});
  };

  const validateSkuField = async (_, value) => {
    const normalizedValue = normalizeComparableSku(value);

    if (!normalizedValue) {
      throw new Error("Inserisci lo SKU del modulo!");
    }

    const duplicateMatch = findDuplicateSku(
      normalizedValue,
      services,
      draftSeed.objectId
    );

    if (duplicateMatch) {
      throw new Error(`SKU già usata da ${duplicateMatch.name}.`);
    }

    if (!hasSkuChanged(normalizedValue, initialSku)) {
      return;
    }

    if (!isValidSkuFormat(normalizedValue)) {
      throw new Error(
        "Formato non valido. Usa SERVICE[-MODULE]-TYPE-YY-MM-DD."
      );
    }
  };

  const skuFieldExtra = (
    <Space
      direction="vertical"
      size={8}
      style={{ width: "100%", marginTop: 8 }}
    >
      <SkuWizard
        value={currentSku}
        moduleName={watchedName}
        existingModules={services}
        currentModuleId={draftSeed.objectId}
        onApply={handleWizardApply}
      />

      {skuDuplicateMatch && (
        <Alert
          type="error"
          showIcon
          message={`SKU già usata da ${skuDuplicateMatch.name}.`}
        />
      )}

      {!skuDuplicateMatch && skuFormatInvalid && (
        <Alert
          type="warning"
          showIcon
          message="Formato SKU non valido."
          description="Usa SERVICE[-MODULE]-TYPE-YY-MM-DD oppure apri il wizard per correggerla."
        />
      )}

      {!skuDuplicateMatch && skuLegacyUnchanged && (
        <Alert
          type="info"
          showIcon
          message="SKU legacy mantenuta"
          description="Il valore corrente non segue il nuovo formato, ma resterà valido finché non lo modifichi."
        />
      )}
    </Space>
  );

  const renderServiceOption = (service) => (
    <Select.Option
      key={service.objectId}
      value={service.objectId}
      label={service.name}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span>{service.name}</span>
        {service.sku && (
          <Text type="secondary" style={{ fontFamily: "monospace", fontSize: 11 }}>
            {service.sku}
          </Text>
        )}
        {!service.is_active && <Tag color="error" style={{ marginInlineEnd: 0 }}>inattivo</Tag>}
      </span>
    </Select.Option>
  );

  // Handle file picker
  const openFilePicker = (field, featureIndex = null) => {
    setFilePickerField(field);
    setFilePickerFeatureIndex(featureIndex);
    setFilePickerVisible(true);
  };

  const handleFileSelect = (url) => {
    if (filePickerFeatureIndex !== null) {
      // Update feature image
      const newFeatures = [...features];
      newFeatures[filePickerFeatureIndex].image = url;
      setFeatures(newFeatures);
      form.setFieldValue('features', newFeatures);
    } else {
      // Update cover_image_url or other field
      form.setFieldValue(filePickerField, url);
    }
    setFilePickerVisible(false);
  };

  // Handle features
  const addFeature = () => {
    const newFeatures = [...features, { title: '', description: '', image: '' }];
    setFeatures(newFeatures);
    form.setFieldValue('features', newFeatures);
  };

  const removeFeature = (index) => {
    const newFeatures = features.filter((_, i) => i !== index);
    setFeatures(newFeatures);
    form.setFieldValue('features', newFeatures);
  };

  const updateFeature = (index, field, value) => {
    const newFeatures = [...features];
    newFeatures[index][field] = value;
    setFeatures(newFeatures);
    form.setFieldValue('features', newFeatures);
  };

  const handleHtmlDescriptionChange = (value) => {
    // Store the raw value when editing
    setHtmlDescription(value);
    form.setFieldValue('html_description', value);
  };

  const validateIncludedQuantityMaxStepMultiplier = (_, value) => {
    if (!form.getFieldValue("included_quantity_max_step")) {
      return Promise.resolve();
    }

    if (Number(value) > 0) {
      return Promise.resolve();
    }

    return Promise.reject(
      new Error("Inserisci un moltiplicatore maggiore di 0")
    );
  };

  return (
    <Form
      form={form}
      name="module_edit"
      layout="horizontal"
      onFieldsChange={handleFieldsChange}
    >
      <Row gutter={32}>
        <Col span={9}>
          <Divider orientation="left">Info</Divider>
          <Form.Item
            {...layout}
            name="name"
            label="Nome"
            rules={[
              { required: true, message: "Inserisci il nome del modulo!" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            {...layout}
            name="sku"
            label="SKU"
            extra={skuFieldExtra}
            rules={[
              { required: true, message: "Inserisci lo SKU del modulo!" },
              { validator: validateSkuField },
            ]}
          >
            <Input
              placeholder="SERVICE[-MODULE]-TYPE-YY-MM-DD"
              onBlur={handleSkuBlur}
            />
          </Form.Item>

          <Form.Item
            {...layout}
            name="description"
            label={
              <span>
                Descrizione per preventivo{" "}
                <Tooltip title="Descrizione che apparirà nei preventivi">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            {...layout}
            name="price"
            label="Prezzo"
            rules={[{ required: true, message: "Inserisci il prezzo!" }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              formatter={formatPrice}
              parser={parsePrice}
              disabled={quantityEnabled || isProductLine}
            />
          </Form.Item>

          <Form.Item {...layout} name="order" label="Ordine">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            {...layout}
            label="Icona"
          >
            {iconUrl ? (
              <div style={{
                padding: "8px 12px",
                border: "1px solid #d9d9d9",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}>
                <img src={iconUrl} alt="icon" style={{ width: 32, height: 32, objectFit: "contain" }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
                  {iconFileName || "Icona"}
                </span>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => updateIcon(null, null)}
                  size="small"
                />
              </div>
            ) : (
              <FileUploader
                onComplete={(url, name) => updateIcon(url, name)}
                onUploadStart={() => {}}
                allowedFileTypes={["image/*", ".svg"]}
                maxFileSize={5 * 1024 * 1024}
                maxNumberOfFiles={1}
                note="Immagine (PNG, SVG, JPG), max 5 MB"
                height={150}
                collection="modules"
              />
            )}
            <Space size={4} wrap style={{ marginTop: 8 }}>
              <Button
                type="link"
                size="small"
                icon={<RocketOutlined />}
                style={{ padding: 0, height: "auto" }}
                onClick={() => {
                  const categoryId = form.getFieldValue(
                    "module_category_pointer",
                  );
                  const params = new URLSearchParams({ builder: "1" });

                  if (categoryId) {
                    params.set("category", categoryId);
                  }

                  if (moduleId) {
                    params.set("module", moduleId);
                  }

                  window.open(
                    `/settings/icons?${params.toString()}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                Genera icona con AI
              </Button>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                style={{ padding: 0, height: "auto" }}
                onClick={() => {
                  setCopySourceId(null);
                  setCopyModalOpen(true);
                }}
              >
                Copia da modulo
              </Button>
            </Space>
            <Modal
              title="Copia icona da un modulo"
              open={copyModalOpen}
              onCancel={() => setCopyModalOpen(false)}
              okText="Copia icona"
              okButtonProps={{
                disabled: !services?.find(
                  (service) => service.objectId === copySourceId,
                )?.icon?.url,
              }}
              onOk={() => {
                const source = services?.find(
                  (service) => service.objectId === copySourceId,
                );

                if (source?.icon?.url) {
                  updateIcon(source.icon.url, source.icon.name);
                }

                setCopyModalOpen(false);
                setCopySourceId(null);
              }}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Cerca un modulo con icona..."
                style={{ width: "100%" }}
                value={copySourceId}
                onChange={setCopySourceId}
                notFoundContent="Nessun modulo con icona"
                options={(services || [])
                  .filter(
                    (service) =>
                      service.icon?.url && service.objectId !== moduleId,
                  )
                  .map((service) => ({
                    value: service.objectId,
                    label: service.name,
                    service,
                  }))}
                optionRender={(option) => (
                  <Space>
                    <img
                      src={option.data.service.icon.url}
                      alt={option.data.label}
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                    <span>{option.data.label}</span>
                  </Space>
                )}
              />
              {(() => {
                const source = services?.find(
                  (service) => service.objectId === copySourceId,
                );

                if (!source?.icon?.url) {
                  return null;
                }

                return (
                  <div
                    style={{
                      marginTop: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <img
                      src={source.icon.url}
                      alt={source.name}
                      style={{ width: 56, height: 56, objectFit: "contain" }}
                    />
                    <Typography.Text type="secondary">
                      Questa icona verrà copiata nel modulo corrente.
                    </Typography.Text>
                  </div>
                );
              })()}
            </Modal>
          </Form.Item>
        </Col>

        <Col span={7}>
          <Divider orientation="left">Quantità e altro</Divider>
          <Form.Item
            {...layout}
            name="included_quantity"
            label="Quantità Inclusa"
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              disabled={isProductLine || includedQuantityMaxStepEnabled}
            />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="included_quantity_max_step_multiplier"
            label={
              <span>
                Moltiplicatore Max Step{" "}
                <Tooltip title="Moltiplica il range massimo dello scaglione per calcolare la quantità inclusa nel quotatore">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            rules={[
              { validator: validateIncludedQuantityMaxStepMultiplier },
            ]}
          >
            <InputNumber
              min={0.01}
              step={0.1}
              style={{ width: "100%" }}
              disabled={isProductLine || !includedQuantityMaxStepEnabled}
            />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="included_quantity_max_step_minimum"
            label={
              <span>
                Minimo Max Step{" "}
                <Tooltip title="Quantità minima da applicare se il calcolo range massimo per moltiplicatore produce un valore inferiore">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
          >
            <InputNumber
              min={0}
              step={0.1}
              style={{ width: "100%" }}
              disabled={isProductLine || !includedQuantityMaxStepEnabled}
            />
          </Form.Item>

          <Form.Item
            {...layout}
            name="unit_of_measure"
            label="Unità di Misura"
          >
            <Input placeholder="es. utenti, sedi, GB..." disabled={isProductLine} />
          </Form.Item>

          <Form.Item
            {...layout}
            name="price_for_quantity"
            label="Prezzo per Quantità"
          >
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              formatter={formatPrice}
              parser={parsePrice}
              disabled={!quantityEnabled || isProductLine}
            />
          </Form.Item>

          <Form.Item
            {...layout}
            name="minutes_of_training"
            label="Minuti di Formazione"
          >
            <InputNumber min={0} style={{ width: "100%" }} disabled={isProductLine}/>
          </Form.Item>

          <Form.Item {...layout} name="power_by" label="Powered By Nome">
            <Input disabled={isProductLine} />
          </Form.Item>

          <Form.Item {...layout} name="power_by_link" label="Powered By Link">
            <Input disabled={isProductLine} />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Divider orientation="left">Configurazione</Divider>
          <Form.Item
            {...layoutWithLongLabels}
            name="is_active"
            label={
              <span>
                Attivo{" "}
                <Tooltip title="Indica se il modulo è attualmente disponibile per l'acquisto">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch defaultChecked />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="is_product_line"
            label={
              <span>
                Linea di Prodotto{" "}
                <Tooltip title="Imposta questo modulo come linea di prodotto principale (ad es. Eterno, FuneralCloud, Add-On)">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="is_main"
            label={
              <span>
                Modulo Principale{" "}
                <Tooltip title="">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch disabled={isProductLine} />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="una_tantum"
            label={
              <span>
                Una Tantum{" "}
                <Tooltip title="Il servizio viene addebitato una sola volta">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch disabled={isProductLine} />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="quantity"
            label={
              <span>
                Quantità{" "}
                <Tooltip title="Abilita la gestione della quantità per questo modulo">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch disabled={isProductLine} />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="charge_by_quantity"
            label={
              <span>
                Addebito per Quantità{" "}
                <Tooltip title="Il prezzo del modulo viene moltiplicato per la quantità">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch disabled={isProductLine} />
          </Form.Item>

          <Form.Item
            {...layoutWithLongLabels}
            name="included_quantity_max_step"
            label={
              <span>
                Quantità Inclusa Max Step{" "}
                <Tooltip title="Imposta la quantità inclusa al valore massimo dello scaglione di appartenenza">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            valuePropName="checked"
          >
            <Switch disabled={isProductLine} />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Divider orientation="left">Relazioni</Divider>
          {relationshipWarnings.length > 0 && (
            <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
              {relationshipWarnings.map((warning) => (
                <Alert
                  key={warning.key}
                  type={warning.type}
                  showIcon
                  message={warning.message}
                />
              ))}
            </Space>
          )}

          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="module_category_pointer"
            label="Categoria"
          >
            <Select
              allowClear
              placeholder="Seleziona una categoria"
              optionFilterProp="children"
              showSearch
              disabled={isProductLine}
            >
              {categories.map((category) => (
                <Select.Option
                  key={category.objectId}
                  value={category.objectId}
                >
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="includedServices"
            label="Moduli Inclusi"
            extra="Questi moduli vengono attivati in automatico insieme a questo modulo."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Seleziona i moduli inclusi"
              optionFilterProp="label"
              showSearch
              disabled={isProductLine}
            >
              {serviceGroups.map((group) => (
                <Select.OptGroup key={`included-${group.label}`} label={group.label}>
                  {group.items.map(renderServiceOption)}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="targets"
            label="Target"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Seleziona i target"
              optionFilterProp="children"
              showSearch
            >
              {moduleTargets?.map((target) => (
                <Select.Option key={target.objectId} value={target.objectId}>
                  {target.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="relatedServices"
            label={relatedLabel}
            extra={
              isProductLine
                ? "Qui definisci i moduli principali che compongono il servizio e che diventano visibili nel quotatore."
                : "Qui definisci moduli suggeriti o collegati che emergono quando il modulo è selezionato."
            }
          >
            <Select
              mode="multiple"
              allowClear
              placeholder={relatedPlaceholder}
              optionFilterProp="label"
              showSearch
            >
              {serviceGroups.map((group) => (
                <Select.OptGroup key={`related-${group.label}`} label={group.label}>
                  {group.items.map(renderServiceOption)}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <div id="quantityError" style={errorMessageStyle}>
            Attenzione: Quantità e Quantità Inclusa Max Step non possono essere
            abilitati contemporaneamente
          </div>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Divider orientation="left">Media e Contenuti</Divider>
          
          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="html_description"
            label={
              <span>
                Descrizione Upselling{" "}
                <Tooltip title="Descrizione formattata con supporto per bold, liste, ecc. che apparirà nella pagina di upselling">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
            extra={
              <Button
                size="small"
                onClick={() => setShowHtmlSource(!showHtmlSource)}
                style={{ marginTop: 8 }}
              >
                {showHtmlSource ? 'Visual Editor' : 'Visualizza HTML'}
              </Button>
            }
          >
            <div style={{ backgroundColor: 'white' }}>
              <style>
                {`
                  .custom-quill-editor .ql-container {
                    min-height: 120px;
                  }
                  .custom-quill-editor .ql-editor {
                    min-height: 120px;
                  }
                  .custom-quill-editor .ql-editor > * {
                    margin-bottom: 0;
                  }
                  .custom-quill-editor .ql-editor:not(.ql-blank)::before {
                    content: none;
                  }
                  .custom-quill-editor .ql-editor.ql-blank::before {
                    font-style: italic;
                    color: rgba(0, 0, 0, 0.6);
                    content: attr(data-placeholder);
                    pointer-events: none;
                    position: absolute;
                  }
                `}
              </style>
              {showHtmlSource ? (
                <TextArea
                  value={htmlDescription}
                  onChange={(e) => handleHtmlDescriptionChange(e.target.value)}
                  rows={6}
                  style={{ fontFamily: 'monospace' }}
                />
              ) : (
                <ReactQuill 
                  theme="snow" 
                  modules={quillModules}
                  formats={quillFormats}
                  className="custom-quill-editor"
                  value={htmlDescription}
                  onChange={handleHtmlDescriptionChange}
                  placeholder="Inserisci la descrizione..."
                />
              )}
            </div>
          </Form.Item>
          
          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="youtube_video_url"
            label="YouTube Video URL"
          >
            <Input placeholder="https://www.youtube.com/watch?v=..." />
          </Form.Item>

          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="landing_page_url"
            label={
              <span>
                Module Website Page{" "}
                <Tooltip title="URL della pagina dedicata al modulo sul sito web">
                  <InfoCircleOutlined style={{ color: "#1890ff" }} />
                </Tooltip>
              </span>
            }
          >
            <Input placeholder="https://example.com/module-page" />
          </Form.Item>

          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            name="cover_image_url"
            label="Cover Image"
          >
            <Space>
              <Input 
                placeholder="URL dell'immagine di copertina"
                value={form.getFieldValue('cover_image_url')}
                style={{ width: 400 }}
              />
              <Button
                icon={<FileImageOutlined />}
                onClick={() => openFilePicker('cover_image_url')}
              >
                Seleziona da CDN
              </Button>
            </Space>
          </Form.Item>

          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            label="Features"
          >
            <List
              dataSource={features}
              locale={{ emptyText: 'Nessuna feature aggiunta' }}
              renderItem={(feature, index) => (
                <Card
                  size="small"
                  style={{ marginBottom: 16 }}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeFeature(index)}
                    />
                  }
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <Input
                        placeholder="Titolo"
                        value={feature.title}
                        onChange={(e) => updateFeature(index, 'title', e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                    </Col>
                    <Col span={16}>
                      <Input
                        placeholder="Descrizione"
                        value={feature.description}
                        onChange={(e) => updateFeature(index, 'description', e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                    </Col>
                    <Col span={24}>
                      <Space>
                        <Input
                          placeholder="URL dell'immagine"
                          value={feature.image}
                          onChange={(e) => updateFeature(index, 'image', e.target.value)}
                          style={{ width: 400 }}
                        />
                        <Button
                          icon={<FileImageOutlined />}
                          onClick={() => openFilePicker('features', index)}
                        >
                          Seleziona da CDN
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              )}
            />
            <Button
              type="dashed"
              onClick={addFeature}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              Aggiungi Feature
            </Button>
          </Form.Item>
        </Col>
      </Row>

      <CDNFilePicker
        visible={filePickerVisible}
        onSelect={handleFileSelect}
        onCancel={() => setFilePickerVisible(false)}
        basePath="modules/"
        fileTypes={['image']}
      />

      {/* Hidden field to store features data */}
      <Form.Item name="features" hidden>
        <Input />
      </Form.Item>
    </Form>
  );
};

export default ModuleEditForm;
